import { Injectable } from '@angular/core'

import { StorageService } from '../../storage.service'
import { DataLayerService } from '../data-layer.service'
import {
  IProductTrack,
  IPurchaseEvent,
  IPurchaseItem,
} from './ecommerce-tracking.interfaces'
import { IReservation } from 'app/shared/models/reservation.interfaces'
import { LocaleService } from 'app/shared/locale-service'
import { ResourceService } from 'app/shared/resources/resource.service'

@Injectable()
export class EcommerceAmendmentTrackingService {
  constructor(
    private dataLayerService: DataLayerService,
    private storageService: StorageService,
    private resourceService: ResourceService,
    private localeService: LocaleService,
  ) {}

  public track(reservation: IReservation): void {
    //Adding 2 secounds delay to allow the data layer to be ready
    setTimeout(() => {
      let purchaseEvent = this.createNewPurchaseTrack(reservation)
      this.dataLayerService.push({ ecommerce: null })
      this.dataLayerService.push(purchaseEvent)
    }, 2000)
  }

  public saveTrackingData(data: IReservation) {
    this.storageService.setSbwAmendmentTracking(JSON.stringify(data))
  }

  private createNewPurchaseTrack(reservation: IReservation): IPurchaseEvent {
    const host = window.location.host.toLowerCase()
    const useUnifiedGTM = host.indexOf('.dfds.com') > 0 || host.indexOf('localhost') >= 0
    const payingPassengers = reservation.passengers.filter((x) => x.customerCategory === 'ADULT' || x.customerCategory === 'CHILD').length
    const ret = reservation.departures[1]
    const outbound = reservation.departures[0]
    const agent = localStorage.getItem('sbw_AgentGenericId') ?? undefined
    const amendmentTracking: IReservation = JSON.parse(this.storageService.getSbwAmendmentTracking())

    const purchaseEvent: IPurchaseEvent = {
      event: 'purchase',
      engagement: {
        entity: agent && agent.length > 0 ? 'agent' : 'user',
        category: 'booking',
        action: 'amendment',
      },
      ecommerce: {
        transaction_id: reservation.reservationCode,
        affiliation: 'SeaBook',
        amended_value: amendmentTracking.remainingAmount ?? 0,
        original_value: amendmentTracking.totalPaid ?? reservation.totalPaid,
        total_value: reservation.totalPrice,
        currency: reservation.currencyCode,
        payment_method: reservation.creditCard,
        shipping: '0',
        tax: '0',
        coupon: reservation.offerCode,
        deposit: false,
        items: this.getPruchaseItems(reservation),
      },
      environment: {
        business_area: 'passenger',
        booking_engine: useUnifiedGTM ? 'seabookAmendment' : 'seabook',
        country_code: this.localeService.getUnifiedCountryCode(),
        language_code: this.localeService.getUnifiedLanguageCode(),
        locale: this.localeService.getLocale(),
      },
      user: {
        id: sessionStorage.getItem('pax_Sub'),
        agent_id: agent,
      },
      properties: {
        passengers: {
          number_adults: reservation.passengers.filter((x) => x.customerCategory === 'ADULT').length,
          number_children: reservation.passengers.filter((x) => x.customerCategory === 'CHILD').length,
          number_infants: reservation.passengers.filter((x) => x.customerCategory === 'INFANT').length,
          pets_selected: outbound.pets?.length > 0,
          number_total: reservation.passengers.length,
          number_outbound: payingPassengers, //This how we do it
          number_return: payingPassengers,
        },
        vehicles: outbound.vehicles.length
          ? outbound.vehicles.map((v) => {
              return {
                included: true,
                type: v.description,
                number: v.count,
                id: v.id,
                trailer_code: v.trailer ? v.trailer.description : undefined,
                pets_allowed: undefined, //We do not have a way to define if pets are allowed
              }
            })
          : [
              {
                included: false,
                type: '',
                number: 0,
                id: '',
                trailer_code: undefined,
                pets_allowed: undefined,
              },
            ],
        trip: {
          type: ret ? 'return' : 'oneway',
          outbound_departure_date: outbound.departureDateTime && outbound.departureDateTime.toString().replace('T', ' '),
          return_departure_date: ret && ret.departureDateTime && ret.departureDateTime.toString().replace('T', ' '),
          route_code: ret ? `${outbound.routeCode} - ${ret.routeCode}` : outbound.routeCode,
          meals_included: reservation.departures.some((d) => d.rooms.length) ? true : false,
          route_name: outbound.routeName,
          outbound_port_name: outbound.routeName,
          return_port_name: ret && ret.routeName,
          outbound_route_code: outbound.routeCode,
          return_route_code: ret && ret.routeCode,
          product_code: reservation.productCode,
          sales_owner: this.resourceService.getSalesOwner(),
          booking_type: reservation.productCode && reservation.productCode[0],
        },
      },
    }
    return purchaseEvent
  }

  private getPruchaseItems(reservation: IReservation): IPurchaseItem[] {
    let products = this.getProduct(reservation)
      .concat(this.getMeals())
      .concat(this.getCabins())
      .concat(this.getVehicles())
      .concat(this.getTpas(reservation))
      .concat(this.getVouchers(reservation))

    let items: IPurchaseItem[] = products.map((product) => {
      let item: IPurchaseItem = {
        item_id: product.id,
        item_name: product.name,
        item_brand: product.brand,
        item_category: product.category,
        item_variant: product.variant,
        price: typeof product.price === 'string' ? parseFloat(product.price) : product.price ?? 0,
        quantity: product.quantity,
        leg: product.dimension1 ?? 'unknown',
        amend: product.dimension5,
      }
      return item
    })
    return items
  }

  private getProduct(reservation: IReservation): IProductTrack[] {
    let products: IProductTrack[] = []
    let product = this.createProduct(reservation, reservation.productCode, reservation.productDescription, 0, 1, 'outbound')
    products.push(product)
    return products
  }

  private getMeals(): IProductTrack[] {
    const mealProducts = <IProductTrack[]>JSON.parse(this.storageService.getSbwAmendmentMealsTracking())
    return mealProducts ? mealProducts : []
  }

  private getTpas(reservation: IReservation): IProductTrack[] {
    let products: IProductTrack[] = []

    reservation.arrangements.forEach((arrangement) => {
      arrangement.arrangementAsts.forEach((ast) => {
        let price = this.getPriceFromPriceItems(reservation, ast.id)
        let product = this.createProduct(reservation, ast.id, ast.description, price, ast.count)
        products.push(product)
      })
    })
    return products
  }

  private getVouchers(reservation: IReservation): IProductTrack[] {
    let products: IProductTrack[] = []

    reservation.departures.forEach((departure) => {
      departure.voucherArrangements.forEach((v) => {
        v.voucherAsts.forEach((ast) => {
          let price = this.getPriceFromPriceItems(reservation, ast.id, departure.departureId)
          let d1 = reservation.departures[0].departureId === departure.departureId ? 'outbound' : 'return'
          let product = this.createProduct(reservation, ast.id, ast.description, price, ast.count, d1)
          products.push(product)
        })
      })
    })
    return products
  }

  private getVehicles(): IProductTrack[] {
    let vehicleProducts = <IProductTrack[]>JSON.parse(this.storageService.getSbwAmendmentVehiclesTracking())  
    if(!vehicleProducts || vehicleProducts.length === 0) {
      return [] 
    }
    const vehicleCount: { [key: string]: IProductTrack } = {}
    vehicleProducts.forEach((vehicle) => {
        const key = `${vehicle.id}_${vehicle.price}_${vehicle.dimension1}_${vehicle.dimension5}`;
        if (vehicleCount[key]) {
          vehicleCount[key].quantity += 1;
        } else {
          vehicleCount[key] = { ...vehicle, quantity: 1 };
        }
      });

    return Object.values(vehicleCount)
  }

  private getCabins(): IProductTrack[] {
    const cabinProducts = <IProductTrack[]>JSON.parse(this.storageService.getSbwAmendmentCabinsTracking())
    return cabinProducts ? cabinProducts : []
  }

  private createProduct(
    reservation: IReservation,
    id: string,
    name: string,
    price: number,
    count: number,
    d1?: string,
    d2?: string,
    d3?: string,
    d4?: string
  ): IProductTrack {
    let product = <IProductTrack>{
      brand: 'SeaBook',
      name: name,
      id: id,
      price: price.toFixed(2),
      variant: reservation.productCode[0],
      category: this.getCategory(reservation),
      quantity: count,
      dimension1: d1,
      dimension2: d2,
      dimension3: d3,
      dimension4: d4,
    }
    return product
  }

  private getPriceFromPriceItems(reservation: IReservation, id: string, departureId?: string): number {
    if (departureId) {
      let result = reservation.priceItems.find(
        (x) => x.departureId != null && x.departureId.toLowerCase() === departureId.toLowerCase() && x.id.toLowerCase() === id.toLowerCase()
      )
      if (result) {
        return result.price
      }
    }
    let result2 = reservation.priceItems.find((x) => x.id.toLowerCase() === id.toLowerCase())
    return result2 ? result2.price : 0
  }

  private getCategory(reservation: IReservation): string {
    if (!reservation.routeCode) {
      return ''
    }
    if (reservation.routeCode.length !== 4) {
      return reservation.routeCode
    }
    return `${reservation.routeCode}-${reservation.routeCode.substring(2)}${reservation.routeCode.substring(0, 2)}`
  }
}
